import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Bell, Calendar, Mail, Rocket } from "lucide-react";
import { SiteHeader } from "@/components/dashboard/site-header";
import Footer from "@/components/home/FooterSection";
import { useNavigate } from "react-router-dom";

const ComingSoon = ({
	title = "Coming Soon",
	description = "We're working hard to bring you something amazing. Stay tuned!",
	estimatedDate,
	showEmailSignup = false,
	showBackButton = true,
	backUrl,
	icon,
	features = [],
	className = "",
}) => {
	const [email, setEmail] = useState("");
	const [isSubscribed, setIsSubscribed] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const navigate = useNavigate();

	const handleEmailSubmit = async (e) => {
		e.preventDefault();
		if (!email) return;

		setIsLoading(true);

		// Simulate API call
		await new Promise((resolve) => setTimeout(resolve, 1000));

		setIsSubscribed(true);
		setIsLoading(false);
		setEmail("");
	};

	const handleGoBack = () => {
		if (backUrl) {
			navigate(backUrl);
		} else {
			navigate(-1);
		}
	};

	return (
		<div className="flex min-h-screen flex-col">
			<SiteHeader />
			<div
				className={`min-h-screen flex items-center justify-center p-4 ${className}`}>
				<div className="max-w-2xl w-full text-center space-y-8">
					{/* Icon */}
					<div className="flex justify-center">
						<div className="w-24 h-24 bg-yellow-500 rounded-full flex items-center justify-center shadow-lg">
							{icon || (
								<Rocket className="w-12 h-12 text-white" />
							)}
						</div>
					</div>

					{/* Main Content */}
					<div className="space-y-4">
						<h1 className="text-4xl md:text-5xl font-bold text-gray-900">
							{title}
						</h1>
						<p className="text-xl text-gray-600 max-w-lg mx-auto">
							{description}
						</p>
					</div>

					{/* Estimated Date */}
					{estimatedDate && (
						<Card className="max-w-md mx-auto">
							<CardContent className="p-4">
								<div className="flex items-center justify-center gap-2 text-yellow-600">
									<Calendar className="w-5 h-5" />
									<span className="font-medium">
										Expected Launch: {estimatedDate}
									</span>
								</div>
							</CardContent>
						</Card>
					)}

					{/* Features Preview */}
					{features.length > 0 && (
						<div className="space-y-4">
							<h3 className="text-lg font-semibold text-gray-800">
								What to expect:
							</h3>
							<div className="flex flex-wrap justify-center gap-2">
								{features.map((feature, index) => (
									<Badge
										key={index}
										variant="secondary"
										className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200">
										{feature}
									</Badge>
								))}
							</div>
						</div>
					)}

					{/* Email Signup */}
					{showEmailSignup && (
						<Card className="max-w-md mx-auto">
							<CardContent className="p-6">
								{!isSubscribed ? (
									<form
										onSubmit={handleEmailSubmit}
										className="space-y-4">
										<div className="flex items-center gap-2 text-gray-700 mb-4">
											<Bell className="w-5 h-5" />
											<span className="font-medium">
												Get notified when we launch
											</span>
										</div>
										<div className="flex gap-2">
											<Input
												type="email"
												placeholder="Enter your email"
												value={email}
												onChange={(e) =>
													setEmail(e.target.value)
												}
												required
												className="flex-1"
											/>
											<Button
												type="submit"
												disabled={isLoading}
												className="bg-yellow-500 hover:bg-yellow-600 text-black">
												{isLoading ? (
													<div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
												) : (
													<Mail className="w-4 h-4" />
												)}
											</Button>
										</div>
									</form>
								) : (
									<div className="text-center space-y-2">
										<div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
											<Bell className="w-6 h-6 text-green-600" />
										</div>
										<p className="text-green-600 font-medium">
											Thanks! We'll notify you when it's
											ready.
										</p>
									</div>
								)}
							</CardContent>
						</Card>
					)}

					{/* Back Button */}
					{showBackButton && (
						<Button
							variant="outline"
							onClick={handleGoBack}
							className="inline-flex items-center gap-2">
							<ArrowLeft className="w-4 h-4" />
							Go Back
						</Button>
					)}

					{/* Footer */}
					<div className="text-sm text-gray-500 pt-8">
						<p>
							Have questions?{" "}
							<a
								href="/contact"
								className="text-yellow-600 hover:text-yellow-700 underline">
								Contact us
							</a>
						</p>
					</div>
				</div>
			</div>
			<Footer />
		</div>
	);
};

export default ComingSoon;
